import { Fragment, useState } from 'react'
import { useAtomValue } from 'jotai'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import Fade from '@mui/material/Fade'

import LoadingBox from "@/components/loading-box"
import ErrorBox from "@/components/error-box"
import MunicipalityVisibilityForm from './forms/municipality-score-visibility'
import MunicipalityPublicVisibilityForm from './forms/municipality-public-visibility'
import { useMunicipalityAdministrationList } from '@/hooks/api/use-municipality'
import { UserRole } from '@/types'
import PermissionsGate from './permissions-gate'
import MunicipalitySharingLimitationForm from './forms/municipality-sharing-limitation'
import { selectedCollectionCompanyIdAtom } from '@/state'
import MunicipalityForm from './forms/municipality'
import BooleanLabel from './boolean-label'

function MunicipalityAdministrationList() {

    const [selectedId, setSelectedId] = useState<number | null>(null)
    const [create, setCreate] = useState<boolean>(false)

    const collectionCompanyId = useAtomValue(selectedCollectionCompanyIdAtom)
    
    const { status, data, error } = useMunicipalityAdministrationList(collectionCompanyId)

    if (status === 'pending') {
        return (
            <LoadingBox />
        )
    }

    if (error) {
        return <ErrorBox
            error={error}
            message={'Nepovedlo se načíst seznam obcí'}
        />
    }

    const selectedEntry = data.find(entry => entry.id === selectedId)

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            overflowX: 'auto'
        }}>
            <Box sx={{
                overflowX: 'auto',
            }}>
                {data.length === 0 ?
                    <Box px={2.5}>
                        Seznam je prázdný
                    </Box>
                :
                    <Box>
                        <Table
                            size="small"
                            aria-label="Seznam obcí"
                            sx={{
                                mt: 1,
                                minWidth: 600,
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ paddingLeft: 3 }}>ID</TableCell>
                                    <TableCell>Pořadí</TableCell>
                                    <TableCell>Obec</TableCell>
                                    <TableCell>Kg SKO/občan</TableCell>
                                    <TableCell>Zobrazeno skóre na titulní str.</TableCell>
                                    <TableCell>Je na veřejném portálu</TableCell>
                                    <TableCell>Sdílí správu obce</TableCell>
                                    <PermissionsGate userRoles={[ UserRole.admin ]}>
                                        <TableCell>Může zakázat sdílení</TableCell>
                                    </PermissionsGate>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map((row, index) => (
                                    <Fragment key={row.id}>
                                        <TableRow
                                            key={row.id}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                '& > td, th': { borderBottom: 'unset' },
                                                ...(row.id === selectedId ? {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                } : null),
                                            }}
                                            onClick={() => setSelectedId(row.id)}
                                            hover
                                        >
                                            <TableCell component="th" scope="row" sx={{ paddingLeft: 3 }}>
                                            {row.id}
                                            </TableCell>
                                            <TableCell>
                                            {index+1}
                                            </TableCell>
                                            <TableCell>
                                                {row.name}
                                            </TableCell>
                                            <TableCell>
                                                {row.municipality_score?.mixed_weight_per_citizen ?? '-'}
                                            </TableCell>
                                            <TableCell>
                                                <BooleanLabel bool={row.municipality_score?.visible} />
                                            </TableCell>
                                            <TableCell>
                                                <BooleanLabel bool={row.visible_on_public} />
                                            </TableCell>
                                            <TableCell>
                                                <BooleanLabel bool={row.is_visible_for_collection_company} />
                                            </TableCell>
                                            <PermissionsGate userRoles={[ UserRole.admin ]}>
                                                <TableCell>
                                                    <BooleanLabel bool={row.set_is_visible_for_collection_company_by_municipality} />
                                                </TableCell>
                                            </PermissionsGate>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell style={{ padding: 0 }} colSpan={11}>
                                                <Collapse in={row.id === selectedId} timeout="auto" unmountOnExit>
                                                    <Box sx={(theme) => ({
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 1.5,
                                                        p: 1.5,
                                                        justifyContent: 'center',
                                                        borderTop: `1px solid ${theme.palette.customGrey.divider}`
                                                    })}>
                                                        {row.id === selectedId &&
                                                            <Fade in={!!selectedId} style={{ transitionDelay: selectedId ? '200ms' : '0ms', width: '100%' }}>
                                                                <Box position="relative" py={2} key={selectedEntry.id}>
                                                                    <Typography variant="h4" component="h2" mb={2}>
                                                                        {selectedEntry.name}
                                                                    </Typography>
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        flexWrap: 'wrap',
                                                                        gap: 1.5,
                                                                    }}>
                                                                        <MunicipalityVisibilityForm
                                                                            municipalityId={selectedEntry.id}
                                                                            municipalityScore={selectedEntry.municipality_score}
                                                                        />
                                                                        <Box pt={2} />
                                                                        <MunicipalityPublicVisibilityForm
                                                                            municipalityId={selectedEntry.id}
                                                                            municipalityVisible={selectedEntry.visible_on_public}
                                                                        />
                                                                        <Box pt={2} />
                                                                        <MunicipalitySharingLimitationForm
                                                                            municipalityId={selectedEntry.id}
                                                                            municipalityCanLimitSharing={selectedEntry.set_is_visible_for_collection_company_by_municipality}
                                                                        />
                                                                    </Box>
                                                                    <Box sx={{
                                                                        position: 'absolute',
                                                                        top: 0,
                                                                        right: 0,
                                                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                                        borderRadius: '1rem',
                                                                    }}>
                                                                        <IconButton
                                                                            onClick={() => setSelectedId(null)}
                                                                            color="terciary"
                                                                        >
                                                                            <CloseRoundedIcon />
                                                                        </IconButton>
                                                                    </Box>
                                                                </Box>
                                                            </Fade>
                                                        }
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </Fragment>
                                ))}
                                
                            </TableBody>
                        </Table>
                    </Box>
                }
            </Box>
            <PermissionsGate
                userRoles={[
                    UserRole.admin,
                    UserRole.eokoDataAdmin,
                ]}
            >
                <Box px={2.5} py={1.5}>
                    {create ?
                        <Box maxWidth="sm">
                            <MunicipalityForm
                                closeCallback={() => setCreate(false)}
                            />
                        </Box>
                        :
                        <Button
                            variant="contained"
                            color="terciary"
                            startIcon={<AddRoundedIcon />}
                            onClick={() => setCreate(true)}
                        >
                            Přidat novou obec
                        </Button>
                    }
                </Box>
            </PermissionsGate>
        </Box>
    )
}

export default MunicipalityAdministrationList
